import React from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../../hooks/useContent';

const defaultFooter = {
  name: 'ProArk Haven Foundation',
  tagline: 'Touching lives with compassion and hope',
  description: 'A registered charitable foundation in Canada and the United States, touching lives across Nigeria, Canada, and the USA through health, education, and community empowerment.',
  email: 'info@proarkhavenfoundation.org',
  phones: ['437.961.7733', '080.3465.0068'],
  locations: 'Nigeria & Canada',
  locationDetail: 'Multiple Community Centers',
  socialLinks: { facebook: '#', instagram: '#', linkedin: '#' }
};

const fallback = { footer: defaultFooter };

const proarkLinks = [
  { href: '/about', label: 'About Us' },
  { href: '/programs', label: 'Programs' },
  { href: '/impact', label: 'Our Impact' },
  { href: '/partnerships', label: 'Get Involved' },
  { href: '/donate', label: 'Donate' },
  { href: '/contact', label: 'Contact' },
];

const fuwaAleshLinks = [
  { href: '/about', label: 'About FuwaAlesh' },
  { href: '/programs', label: 'FuwaAlesh Programs' },
  { href: '/donate', label: 'Support FuwaAlesh' },
  { href: '/partnerships', label: 'Partner With Us' },
];

const Footer: React.FC = () => {
  const content = useContent('site', fallback);
  const f = content.footer || defaultFooter;

  return (
    <footer>
      {/* Dual Brand Band */}
      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* ProArk Haven — Green Band */}
        <div className="bg-[var(--green)] p-8 md:p-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center shrink-0">
              <svg viewBox="0 0 22 22" fill="none" className="w-5 h-5">
                <path d="M11 2C11 2 4 7 4 13a7 7 0 0014 0C18 7 11 2 11 2z" fill="rgba(255,255,255,0.8)"/>
              </svg>
            </div>
            <div>
              <div className="font-heading text-base font-semibold leading-tight">
                ProArk Haven Foundation
              </div>
              <div className="text-[10px] text-white/50 tracking-widest uppercase">
                Parent Organization
              </div>
            </div>
          </div>
          <p className="text-[13px] text-white/65 leading-relaxed mb-5">
            {f.description || defaultFooter.description}
          </p>
          <div className="flex flex-col gap-1.5">
            {proarkLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className="text-[13px] text-white/50 hover:text-white transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* FuwaAlesh — Brown Band */}
        <div className="bg-sub p-8 md:p-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center shrink-0">
              <svg viewBox="0 0 22 22" fill="none" className="w-5 h-5">
                <circle cx="11" cy="11" r="8" stroke="rgba(255,255,255,0.7)" strokeWidth="1"/>
                <path d="M11 3C11 3 6 7 6 12a5 5 0 0010 0C16 7 11 3 11 3z" fill="rgba(255,255,255,0.4)"/>
              </svg>
            </div>
            <div>
              <div className="font-heading text-base font-semibold leading-tight">
                FuwaAlesh Foundation
              </div>
              <div className="text-[10px] text-white/50 tracking-widest uppercase">
                Subsidiary of ProArk Haven · Nigeria
              </div>
            </div>
          </div>
          <p className="text-[13px] text-white/65 leading-relaxed mb-5">
            A Nigeria-based subsidiary dedicated to Compassion &amp; Hope for Children and the Elderly — bringing care and dignity to the most vulnerable in our communities.
          </p>
          <div className="flex flex-col gap-1.5">
            {fuwaAleshLinks.map(link => (
              <Link
                key={link.href + link.label}
                to={link.href}
                className="text-[13px] text-white/50 hover:text-white transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-deep px-4 md:px-10 py-5 flex flex-col md:flex-row justify-between items-center gap-3 border-t border-white/[0.06]">
        <p className="text-[11.5px] text-white/30 tracking-wide text-center md:text-left">
          © {new Date().getFullYear()} {f.name} · Registered in Canada &amp; USA · Operating in Nigeria, Canada &amp; USA · FuwaAlesh Foundation is a wholly-owned subsidiary (Nigeria)
        </p>
        <div className="flex gap-5">
          <Link to="/privacy" className="text-[11.5px] text-white/30 hover:text-white/70 transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms" className="text-[11.5px] text-white/30 hover:text-white/70 transition-colors">
            Terms of Use
          </Link>
          <Link to="/contact" className="text-[11.5px] text-white/30 hover:text-white/70 transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
